import { useEffect, useMemo, useState, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconChevronDown, IconClose, IconEdit, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';
import { useAuth } from '../auth/useAuth';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import usePaginatedList from '../hooks/usePaginatedList';

interface Filters {
  search: string;
  status: string;
  dateFrom: string;
  dateTo: string;
}

const emptyFilters: Filters = { search: '', status: '', dateFrom: '', dateTo: '' };

interface OvertimeRow {
  id: number;
  user_id: number;
  user_name: string;
  employee_code: string | null;
  date: string;
  hours: number;
  overtime_rate_type_id: number | null;
  rate_type_name: string | null;
  reason: string | null;
  compensation_method: 'pay' | 'leave';
  status: 'pending' | 'approved' | 'rejected';
  decided_by_name: string | null;
  decision_comment: string | null;
  is_pending: boolean;
  approval_levels_required: number;
  current_approval_level: number;
  pending_with_name: string | null;
  approvals: ApprovalHistoryEntry[];
}

interface ApprovalHistoryEntry {
  level: number;
  approver_name: string | null;
  decision: string;
  comment: string | null;
  decided_at: string;
}

interface OvertimeRateTypeOption {
  id: number;
  name: string;
}

interface UserOption {
  id: number;
  full_name: string;
  [key: string]: unknown;
}

const STATUS_BADGE: Record<OvertimeRow['status'], string> = {
  pending: 'badge-scheduled',
  approved: 'badge-active',
  rejected: 'badge-cancelled',
};

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

const emptyForm = {
  user_id: '' as string | number,
  date: '',
  hours: '',
  overtime_rate_type_id: '',
  reason: '',
  compensation_method: 'pay' as 'pay' | 'leave',
};

export function Overtime() {
  const { can } = useAuth();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState<OvertimeRow | null>(null);
  const [editForm, setEditForm] = useState({
    date: '',
    hours: '',
    overtime_rate_type_id: '',
    reason: '',
    compensation_method: 'pay' as 'pay' | 'leave',
  });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [rateTypes, setRateTypes] = useState<OvertimeRateTypeOption[]>([]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    api.get<OvertimeRateTypeOption[]>('/overtime-rate-types').then((res) => setRateTypes(res.data));
  }, []);

  const canUpdate = can('hr.update');
  const canApprove = can('hr.approve');
  const canArchive = can('hr.archive');

  const query = useMemo(
    () => ({
      search: filters.search || undefined,
      status: filters.status || undefined,
      date_from: filters.dateFrom || undefined,
      date_to: filters.dateTo || undefined,
    }),
    [filters]
  );

  const { data, loading, error, setPage, reload } = usePaginatedList<OvertimeRow>('/overtime-requests', query);

  const openAdd = () => {
    setForm(emptyForm);
    setErrors({});
    setAdding(true);
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/overtime-requests', { ...form, user_id: form.user_id || undefined, hours: Number(form.hours) });
      setAdding(false);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const openEdit = (row: OvertimeRow) => {
    setEditing(row);
    setEditForm({
      date: row.date,
      hours: String(row.hours),
      overtime_rate_type_id: row.overtime_rate_type_id ? String(row.overtime_rate_type_id) : '',
      reason: row.reason ?? '',
      compensation_method: row.compensation_method,
    });
    setErrors({});
  };

  const submitEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    try {
      await api.put(`/overtime-requests/${editing.id}`, { ...editForm, hours: Number(editForm.hours) });
      setEditing(null);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const decide = async (row: OvertimeRow, decision: 'approved' | 'rejected') => {
    const verb = decision === 'approved' ? 'Approve' : 'Reject';
    const confirmed = await confirmAction(`${verb} ${row.user_name}'s ${row.hours}h overtime on ${formatDate(row.date)}?`, {
      confirmLabel: verb,
    });
    if (!confirmed) return;
    try {
      await api.post(`/overtime-requests/${row.id}/decide`, { decision });
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : `${verb} failed.`);
    }
  };

  const toggleExpanded = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const remove = async (row: OvertimeRow) => {
    if (!(await confirmAction(`Delete this overtime request for ${row.user_name}?`))) return;
    try {
      await api.delete(`/overtime-requests/${row.id}`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Overtime</h1>
        <button type="button" className="btn btn-primary" onClick={openAdd}>
          + Add
        </button>
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <label className="checkbox-inline">
          Date from
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
          />
        </label>
        <label className="checkbox-inline">
          Date to
          <input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
        </label>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Date</th>
                <th>Hours</th>
                <th>Rate Type</th>
                <th>Paid As</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.flatMap((row) => {
                  const isExpanded = expanded.has(row.id);
                  const rows = [
                    <tr key={row.id}>
                    <td>
                      <button
                        type="button"
                        className="btn icon-btn btn-ghost"
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        onClick={() => toggleExpanded(row.id)}
                      >
                        <IconChevronDown
                          className="icon-btn-svg"
                          style={{ transform: isExpanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s' }}
                        />
                      </button>
                    </td>
                    <td className="muted">{row.employee_code ?? '—'}</td>
                    <td>
                      <strong>{row.user_name}</strong>
                    </td>
                    <td>{formatDate(row.date)}</td>
                    <td>{row.hours}h</td>
                    <td className="muted">{row.rate_type_name ?? '—'}</td>
                    <td>
                      <span className={`badge ${row.compensation_method === 'leave' ? 'badge-active' : 'badge-neutral'}`}>
                        {row.compensation_method === 'leave' ? 'Comp-off' : 'Pay'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[row.status]}`}>{row.status}</span>
                      {row.is_pending && (
                        <>
                          <br />
                          <small className="muted">
                            Pending with: {row.pending_with_name ?? 'no manager set'}
                            {row.approval_levels_required > 1 && ` (level ${row.current_approval_level} of ${row.approval_levels_required})`}
                          </small>
                        </>
                      )}
                    </td>
                    <td className="right">
                      {canApprove && row.is_pending && (
                        <>
                          <IconButton icon={IconCheck} label="Approve" onClick={() => decide(row, 'approved')} />
                          <IconButton
                            icon={IconClose}
                            label="Reject"
                            variant="danger-ghost"
                            onClick={() => decide(row, 'rejected')}
                          />
                        </>
                      )}
                      {canUpdate && <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(row)} />}
                      {canArchive && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(row)} />
                      )}
                    </td>
                  </tr>,
                  ];

                  if (!isExpanded) return rows;

                  if (row.approvals.length === 0) {
                    rows.push(
                      <tr key={`approvals-empty-${row.id}`}>
                        <td></td>
                        <td colSpan={8} className="muted">
                          No decisions recorded yet.
                        </td>
                      </tr>
                    );
                  } else {
                    row.approvals.forEach((a, i) => {
                      rows.push(
                        <tr key={`approval-${row.id}-${i}`}>
                          <td></td>
                          <td colSpan={3} className="muted">
                            Level {a.level} — {a.approver_name ?? 'Unknown'}
                            {a.comment && <>: {a.comment}</>}
                          </td>
                          <td colSpan={2}>
                            <span className={`badge ${a.decision === 'approved' ? 'badge-active' : 'badge-cancelled'}`}>
                              {a.decision}
                            </span>
                          </td>
                          <td colSpan={3} className="muted">
                            {new Date(a.decided_at).toLocaleString('en-GB')}
                          </td>
                        </tr>
                      );
                    });
                  }

                  return rows;
                })
              ) : (
                <tr>
                  <td colSpan={9} className="muted center">
                    No overtime requests yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="Add overtime request" onClose={() => setAdding(false)}>
          <form onSubmit={submitAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            {canUpdate && (
              <label className="span-2">
                Employee
                <SearchableSelect<UserOption>
                  endpoint="/users"
                  value={form.user_id}
                  onChange={(id) => setForm({ ...form, user_id: id })}
                  getLabel={(u) => u.full_name}
                  placeholder="Search staff… (defaults to you)"
                />
                {errors.user_id && <small className="error">{errors.user_id[0]}</small>}
              </label>
            )}
            <label>
              Date *
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              {errors.date && <small className="error">{errors.date[0]}</small>}
            </label>
            <label>
              Hours *
              <input
                type="number"
                step="0.25"
                min="0.25"
                max="24"
                value={form.hours}
                onChange={(e) => setForm({ ...form, hours: e.target.value })}
                required
              />
              {errors.hours && <small className="error">{errors.hours[0]}</small>}
            </label>
            <label className="span-2">
              Rate Type *
              <select
                value={form.overtime_rate_type_id}
                onChange={(e) => setForm({ ...form, overtime_rate_type_id: e.target.value })}
                required
              >
                <option value="">—</option>
                {rateTypes.map((rt) => (
                  <option key={rt.id} value={rt.id}>
                    {rt.name}
                  </option>
                ))}
              </select>
              {errors.overtime_rate_type_id && <small className="error">{errors.overtime_rate_type_id[0]}</small>}
            </label>
            <label className="span-2">
              Compensation
              <select
                value={form.compensation_method}
                onChange={(e) => setForm({ ...form, compensation_method: e.target.value as 'pay' | 'leave' })}
              >
                <option value="pay">Pay</option>
                <option value="leave">Compensatory Off (banked as leave)</option>
              </select>
              {errors.compensation_method && <small className="error">{errors.compensation_method[0]}</small>}
              <small className="muted">
                Compensatory Off converts these hours to leave days at your current shift length, hour-for-hour — the rate type's
                multiplier isn't applied.
              </small>
            </label>
            <label className="span-2">
              Reason
              <textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!form.date || !form.hours || !form.overtime_rate_type_id}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit overtime request for ${editing.user_name}`} onClose={() => setEditing(null)}>
          <form onSubmit={submitEdit} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label>
              Date *
              <input
                type="date"
                value={editForm.date}
                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                required
              />
              {errors.date && <small className="error">{errors.date[0]}</small>}
            </label>
            <label>
              Hours *
              <input
                type="number"
                step="0.25"
                min="0.25"
                max="24"
                value={editForm.hours}
                onChange={(e) => setEditForm({ ...editForm, hours: e.target.value })}
                required
              />
              {errors.hours && <small className="error">{errors.hours[0]}</small>}
            </label>
            <label className="span-2">
              Rate Type *
              <select
                value={editForm.overtime_rate_type_id}
                onChange={(e) => setEditForm({ ...editForm, overtime_rate_type_id: e.target.value })}
                required
              >
                <option value="">—</option>
                {rateTypes.map((rt) => (
                  <option key={rt.id} value={rt.id}>
                    {rt.name}
                  </option>
                ))}
              </select>
              {errors.overtime_rate_type_id && <small className="error">{errors.overtime_rate_type_id[0]}</small>}
            </label>
            <label className="span-2">
              Compensation
              <select
                value={editForm.compensation_method}
                onChange={(e) => setEditForm({ ...editForm, compensation_method: e.target.value as 'pay' | 'leave' })}
              >
                <option value="pay">Pay</option>
                <option value="leave">Compensatory Off (banked as leave)</option>
              </select>
              {errors.compensation_method && <small className="error">{errors.compensation_method[0]}</small>}
            </label>
            <label className="span-2">
              Reason
              <textarea
                rows={3}
                value={editForm.reason}
                onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
